import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const SchoolCard = ({ school, isSelected, isFavorite, onSelect, onToggleFavorite, onViewDetails }) => {
  const getTypeColor = (type) => {
    return type === 'public' ? 'text-green-600 bg-green-50' : 'text-blue-600 bg-blue-50';
  };

  const handleContact = (method) => {
    console.log(`Contacting ${school?.name} via ${method}`);
    // Award exploration points
    console.log('Gamification: +3 points for contacting school');
  };

  return (
    <div className={`bg-white rounded-xl border-2 transition-all ${
      isSelected ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
    } p-4 md:p-6`}>
      <div className="flex items-start gap-3 mb-4">
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(school?.id, e?.target?.checked)}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2 break-words">
                {school?.name}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(school?.type)}`}>
                  {school?.type === 'public' ? 'Public' : 'Privé'}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-600">
                  {school?.accreditation}
                </span>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} color="#F59E0B" fill="#F59E0B" />
                  <span className="text-sm font-semibold text-gray-700">{school?.rating}</span>
                  <span className="text-xs text-gray-500">({school?.reviewCount} avis)</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => onToggleFavorite(school?.id)}
              className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Icon
                name="Heart"
                size={24}
                color={isFavorite ? '#EF4444' : '#9CA3AF'}
                fill={isFavorite ? '#EF4444' : 'none'}
              />
            </button>
          </div>
          <p className="text-sm md:text-base text-gray-600 mb-4">
            {school?.description}
          </p>
        </div>
      </div>

      {/* Location and Student Count */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Icon name="MapPin" size={18} color="#6B7280" />
          <span className="text-gray-700">{school?.location}, {school?.region}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Users" size={18} color="#6B7280" />
          <span className="text-gray-700">{school?.studentCount?.toLocaleString()} étudiants</span>
        </div>
      </div>

      {/* Programs */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Icon name="BookOpen" size={16} />
          Programmes disponibles
        </h4>
        <div className="flex flex-wrap gap-2">
          {school?.programs?.slice(0, 4)?.map((program, index) => (
            <span key={index} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
              {program}
            </span>
          ))}
          {school?.programs?.length > 4 && (
            <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
              +{school?.programs?.length - 4} autres
            </span>
          )}
        </div>
      </div>

      {/* Admission & Fees */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
        <div>
          <h5 className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
            <Icon name="ClipboardCheck" size={14} />
            Admission
          </h5>
          <p className="text-sm text-gray-700">{school?.admissionRequirements}</p>
        </div>
        <div>
          <h5 className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
            <Icon name="Coins" size={14} />
            Frais de scolarité
          </h5>
          <p className="text-sm text-gray-700 font-medium">{school?.tuitionFees}</p>
        </div>
      </div>

      {/* Contact Options */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact direct</h4>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleContact('phone')}
            className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm"
          >
            <Icon name="Phone" size={16} />
            <span className="hidden sm:inline">{school?.contactPhone}</span>
            <span className="sm:hidden">Appeler</span>
          </button>
          <button
            onClick={() => handleContact('email')}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
          >
            <Icon name="Mail" size={16} />
            <span className="hidden sm:inline">{school?.contactEmail}</span>
            <span className="sm:hidden">Email</span>
          </button>
          <a
            href={school?.website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleContact('website')}
            className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm"
          >
            <Icon name="Globe" size={16} />
            <span>Site web</span>
          </a>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="default"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onViewDetails(school)}
          className="flex-1"
        >
          Voir les détails
        </Button>
        <Button
          variant="outline"
          iconName="MapPin"
          iconPosition="left"
          className="flex-1"
        >
          Localiser
        </Button>
      </div>
    </div>
  );
};

export default SchoolCard;